import { useCallback, useEffect } from 'react';

import { useRegionByZipCode } from '@/presenters/hooks/catalog/product/use-region-by-zip-code';
import { useGlobalStore } from '@/presenters/store/global';
import { ZIP_CODE } from '@/utils/constants/cookies';
import { cookies } from '@/utils/cookies';
import { userContext } from '@/utils/user-context';
import { formatZipCode } from '@/utils/zip-code-utils/zip-code-utils';

interface UseRegionCheckerProps {
  zipCode: string;
  zipCodeIsValid: boolean;
  pathname?: string;
  formatted?: boolean;
}

export default function useRegionChecker(props: UseRegionCheckerProps) {
  const {
    zipCode,
    zipCodeIsValid,
    pathname = undefined,
    formatted = false,
  } = props;
  const currentRegion = useGlobalStore((state) => state.session?.region);

  const { data, isFetching, isError, isFetched } = useRegionByZipCode(
    { zipCode },
    { enabled: zipCodeIsValid }
  );

  const recontext = useCallback(async () => {
    if (data?.data.id) {
      await userContext.recontext({
        contextBy: 'zip_code',
        zipCodeRangeId: data.data.id,
        zipCode,
      });

      userContext.clearRegion({ pathname });
    }
  }, [data?.data.id, zipCode, pathname]);

  const getStatus = () => {
    if (currentRegion && data?.data.region) {
      if (currentRegion === data?.data.region) {
        return 'valid';
      }

      return 'invalid';
    }
    return 'idle';
  };

  const status = getStatus();

  useEffect(() => {
    if (status === 'valid') {
      const zipCodeCookieOptions = cookies.createOptions({
        name: ZIP_CODE,
        value: formatted ? formatZipCode(zipCode) : zipCode,
      });

      cookies.client.set(zipCodeCookieOptions);
    }
  }, [status, zipCode, formatted]);

  return {
    data,
    isFetching,
    status,
    recontext,
    isError,
    isFetched,
  };
}
